import { useCallback, useEffect, useRef, useState } from 'react';
import type { ScreensActionModel, IScreensLogging } from '../../../api/types';
import type { IScreensComponent } from '../../../api/screensService/getScreens/types';
import { AtomicItemListType } from '../../../api/screensService/getScreens/enums';
import { getInjectComponents } from '../../../api/screensService/getInjectComponents';
import { useIsForegroundNow } from '../../../hooks/useIsForegroundNow';

export interface InjectedState {
  /** 삽입된 컴포넌트 목록 */
  components: IScreensComponent[];
  /** 삽입 기준 섹션 인덱스 */
  afterIndex: number;
  /** 클릭한 상품의 SNO (원본 entity에서 인덱스 재계산용) */
  itemSno: number;
}

/** inject 시 서버가 내려주는 VIEW 이벤트 (복귀 시 1회 발화용) */
export interface PendingInjectEvent {
  eventName: string;
  eventLogging: IScreensLogging;
}

interface InjectParams {
  action: ScreensActionModel;
  sectionIndex: number;
  itemIndex: number;
  itemSno: number;
}

/** 삽입된 컴포넌트 중 ASSEMBLE 타입이 있는지 확인 */
const hasAssembleComponent = (components: IScreensComponent[]): boolean =>
  components.some(
    c => c.type.itemList === AtomicItemListType.AssembleFilterGoodsList,
  );

/** 상품 리스트 컴포넌트의 열 수 반환 */
const getColumnCount = (component: IScreensComponent): number => {
  switch (component.type.itemList) {
    case AtomicItemListType.ThreeColGoodsList:
    case AtomicItemListType.ThreeColCardList:
      return 3;
    case AtomicItemListType.TwoColGoodsList:
    case AtomicItemListType.TwoColCardList:
    case AtomicItemListType.TwoColOutfitList:
      return 2;
    default:
      return 0;
  }
};

/**
 * 상품 리스트에서 행(row) 기준으로 분리
 * iOS MetaFeedComponentReactorFactory.separatedGoodsReactors와 동일
 */
const splitComponentByRow = (
  component: IScreensComponent,
  itemIndex: number,
): { top: IScreensComponent; bottom: IScreensComponent } | null => {
  const columnCount = getColumnCount(component);
  if (columnCount === 0 || !component.entity.itemList) {
    return null;
  }

  const items = component.entity.itemList;
  const clickedRow = Math.floor(itemIndex / columnCount);
  const splitAt = (clickedRow + 1) * columnCount;

  if (splitAt >= items.length) {
    return null; // 마지막 행이면 분리 불필요
  }

  return {
    // iOS: topComponentReactor.footer = nil
    top: {
      ...component,
      type: { ...component.type, footer: null },
      entity: { ...component.entity, itemList: items.slice(0, splitAt), footer: null },
    },
    // iOS: bottomComponentReactor.header = nil
    bottom: {
      ...component,
      type: { ...component.type, header: null },
      entity: { ...component.entity, itemList: items.slice(splitAt), header: null },
    },
  };
};

/**
 * on_return 캐러셀 삽입 훅
 * - 상품 클릭 시 inject API 호출 → 즉시 리스트에 삽입
 * - 행 단위 리스트 분리 지원
 * - ASSEMBLE 재삽입 시 기존 ASSEMBLE만 제거 (PROXY 등 다른 on_return에 영향 방지)
 *   iOS MetaFeedReactor.registerOnReturnActionModel cleanup 로직 대응
 */
export const useOnReturnInject = () => {
  const [injected, setInjected] = useState<InjectedState | null>(null);
  const injectedRef = useRef<InjectedState | null>(null);
  const pendingEventRef = useRef<PendingInjectEvent | null>(null);
  // background에서 inject 응답이 도착했을 때 setInjected를 보류해 두는 ref.
  // 사용자 복귀 시점에 적용되어 carousel mount가 user view 시점과 정렬되도록 함.
  const pendingInjectRef = useRef<InjectedState | null>(null);

  // inject 응답 도착 시점에 이미 foreground면 즉시 VIEW를 발사하기 위해 ref로 동기화.
  // 빠른 복귀로 useOnForeground가 비어있는 pendingEvent를 consume해 영구 소실되던 케이스를 막음.
  const isForeground = useIsForegroundNow();
  const isForegroundRef = useRef(isForeground);
  isForegroundRef.current = isForeground;

  // 상품 클릭 시 호출 - inject API 호출 후 즉시 삽입
  const fetchAndInject = useCallback(
    async (params: InjectParams) => {
      if (params.action.type !== 'INJECT' || !params.action.data?.url) {
        return;
      }

      // ASSEMBLE INJECT인 경우에만 기존 injected 즉시 제거
      // (PROXY 등 다른 on_return에 영향 방지 — iOS 동일 로직)
      if (
        injectedRef.current &&
        hasAssembleComponent(injectedRef.current.components)
      ) {
        setInjected(null);
        injectedRef.current = null;
      }

      try {
        const result = await getInjectComponents(params.action.data.url);
        if (result.components.length === 0) return;

        // VIEW 이벤트는 즉시 발사하지 않고 항상 pending에 저장한다.
        // 상품 클릭 직후엔 이탈(viewDidDisappear)이 비동기라 isForeground가 잠시
        // true로 남아, 즉시 발사 시 캐러셀이 보이기도 전에 미리 찍히는 문제가 있었다.
        // inject 캐러셀이 실제 viewport에 노출될 때 AtomicRenderer가 consume해 발사한다
        // (iOS willDisplayCell 정렬).
        if (result.eventName && result.eventLogging) {
          pendingEventRef.current = {
            eventName: result.eventName,
            eventLogging: result.eventLogging,
          };
        }

        const newState: InjectedState = {
          components: result.components,
          afterIndex: params.sectionIndex,
          itemSno: params.itemSno,
        };
        // foreground면 즉시 적용. background면 pending에 보류해 사용자 복귀 시점에 적용.
        // iOS willDisplayCell처럼 carousel mount 시점이 user view 시점과 정렬되어
        // 내부 FlatList의 onViewableItemsChanged가 자연 발사 → DISPLAY_GOODS 정상 트리거.
        if (isForegroundRef.current) {
          injectedRef.current = newState;
          setInjected(newState);
        } else {
          pendingInjectRef.current = newState;
        }
      } catch {
        // 네트워크 에러 시 무시
      }
    },
    [],
  );

  // 복귀 시 pending 이벤트 소비 (1회 발화 후 초기화)
  const consumePendingEvent = useCallback((): PendingInjectEvent | null => {
    const event = pendingEventRef.current;
    pendingEventRef.current = null;
    return event;
  }, []);

  // 데이터 변경 시 초기화 (새 페이지 로드 등)
  const resetInjected = useCallback(() => {
    setInjected(null);
    injectedRef.current = null;
    pendingEventRef.current = null;
    pendingInjectRef.current = null;
  }, []);

  // foreground 전이 시 보류된 inject 적용.
  // background에서 도착한 응답이 사용자 복귀 시점에 setInjected → carousel mount로 이어지도록.
  useEffect(() => {
    if (isForeground && pendingInjectRef.current) {
      const state = pendingInjectRef.current;
      pendingInjectRef.current = null;
      injectedRef.current = state;
      setInjected(state);
    }
  }, [isForeground]);

  return { injected, fetchAndInject, resetInjected, splitComponentByRow, consumePendingEvent };
};
